import { MILLISECONDS_PER_SECOND } from '@/utils/constants/time';

export function getSecondsFromMS(ms: number): number {
    if (Number.isNaN(ms)) {
        return 0;
    }
    return Math.round(ms / MILLISECONDS_PER_SECOND);
}

export function getSecondsInMS(seconds: number): number {
    return seconds * MILLISECONDS_PER_SECOND;
}

export function getMinsInMS(mins: number): number {
    return mins * 60 * 1000;
}

export function getHoursInMS(hours: number): number {
    return hours * getMinsInMS(60);
}

export function getDaysInMS(days: number): number {
    return days * getHoursInMS(24);
}

export function parseTimestampMs(timestamp?: string | null): number {
    if (!timestamp) {
        return Number.NaN;
    }
    const numeric = Number(timestamp);
    return Number.isFinite(numeric) ? numeric : Date.parse(timestamp);
}

/** Parses a numeric or string value into a positive duration in seconds. */
export function parsePositiveSeconds(value: unknown): number | undefined {
    if (typeof value === 'number' && value > 0) {
        return value;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        const parsed = Number.parseInt(trimmed, 10);

        if (parsed > 0 && String(parsed) === trimmed) {
            return parsed;
        }
    }

    return undefined;
}

export function isToday(date: Date): boolean {
    const today = new Date();
    return getDayDifference(today, date) === 0;
}

export function isInNext24Hours(date: Date, referenceNow: Date = new Date()): boolean {
    const compare = addDays(referenceNow, 1);
    const diffInMS = compare.valueOf() - date.valueOf();
    const dayInMS = getDaysInMS(1);

    return diffInMS > 0 && diffInMS <= dayInMS;
}

export function getDayDifference(d1: Date, d2: Date): number {
    // Create a new date so it doesn't change time for the reference
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    const dayInMS = getDaysInMS(1);

    // Reset time part for accurate comparison
    date1.setHours(0, 0, 0, 0);
    date2.setHours(0, 0, 0, 0);

    const timeDifference = date2.valueOf() - date1.valueOf();
    const dayDifference = Math.ceil(timeDifference / dayInMS);

    return dayDifference;
}

export function addDays(date: Date, days: number): Date {
    const daysInMS = days * getDaysInMS(1);
    const timestamp = date.valueOf() + daysInMS;

    return new Date(timestamp);
}

export function subtractDays(date: Date, days: number): Date {
    const daysInMS = days * getDaysInMS(1);
    const timestamp = date.valueOf() - daysInMS;

    return new Date(timestamp);
}

export type ElapsedUnitLabels = { hours: string; minutes: string; seconds: string };

const DEFAULT_ELAPSED_UNIT_LABELS: ElapsedUnitLabels = { hours: 'h', minutes: 'm', seconds: 's' };

/**
 * Formats the time elapsed since a stream started, e.g. `45s`, `29m`, `1h 29m`.
 * @param streamStartDateTime - Start time in milliseconds since the epoch.
 * @param nowMs - Current time in milliseconds (injectable for tests).
 * @param unitLabels - Abbreviated unit labels (translated by callers that render to the UI).
 */
export function formatElapsedSince(
    streamStartDateTime: number,
    nowMs: number = Date.now(),
    unitLabels: ElapsedUnitLabels = DEFAULT_ELAPSED_UNIT_LABELS,
): string {
    const streamStartInSeconds = Math.floor(streamStartDateTime / 1000);
    let elapsedTime = Math.floor(nowMs / 1000) - streamStartInSeconds;

    const tokens: Record<number, string> = {
        3600: unitLabels.hours,
        60: unitLabels.minutes,
    };

    let result = '';

    for (const [unit, text] of Object.entries(tokens).sort(
        (a, b) => Number.parseInt(b[0]) - Number.parseInt(a[0]),
    )) {
        const unitValue = Number.parseInt(unit, 10);
        if (elapsedTime >= unitValue) {
            const numberOfUnits = Math.floor(elapsedTime / unitValue);
            result += `${numberOfUnits}${text} `;
            elapsedTime -= numberOfUnits * unitValue;
        }
    }

    // Handle cases where elapsed time is less than 60 seconds
    if (!result) {
        result = `${elapsedTime}${unitLabels.seconds}`;
    }

    return result.trim();
}

/**
 * Airing progress as a whole percent (0-100) between a start and end time.
 * Returns undefined when either bound is invalid or the end is not after the start.
 * @param startMs - Airing start in milliseconds since the epoch.
 * @param endMs - Airing end in milliseconds since the epoch.
 * @param nowMs - Current time in milliseconds (injectable for tests).
 */
export function getAiringProgressPercent(
    startMs: number,
    endMs: number,
    nowMs: number = Date.now(),
): number | undefined {
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
        return undefined;
    }

    const percent = ((nowMs - startMs) / (endMs - startMs)) * 100;
    return Math.round(Math.min(Math.max(percent, 0), 100));
}
